import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { NumericFormatterId } from '../../../util/formatters/NumericFormatters';
import { ArrayFormatterId } from '../../../util/formatters/ArrayFormattersConfig';
import { BooleanFormatterId } from '../../../util/formatters/BooleanFormatters';
import { ISourcingCompanyRow, ISourcingPersonRow } from '../../../schemas/SourcingListItems.schema';
import { ExpandAllHeader } from '../components/ExpandAllHeader';
import { SourcingCompanyNameLink } from '../components/SourcingCompanyNameLink';
import { FundingStageCellRenderer } from '../components/FundingStageCellRenderer';

interface SourcingColDefs {
  company: ColDef<ISourcingCompanyRow>[];
  person: ColDef<ISourcingPersonRow>[];
}

export function useSourcingColDefs(): SourcingColDefs {
  return useMemo(() => {
    const agFmt = AgFormatterService.get();
    const currencyShort = agFmt.getFormatterForId<ISourcingCompanyRow, number>(
      NumericFormatterId.currencyShort
    );
    const integer = agFmt.getFormatterForId<ISourcingCompanyRow, number>(NumericFormatterId.integer);
    const companyStringArray = agFmt.getFormatterForId<ISourcingCompanyRow, string[]>(
      ArrayFormatterId.stringArray
    );
    const personStringArray = agFmt.getFormatterForId<ISourcingPersonRow, string[]>(
      ArrayFormatterId.stringArray
    );
    const yesNo = agFmt.getFormatterForId<ISourcingCompanyRow, boolean>(BooleanFormatterId.yesNo);

    const company: ColDef<ISourcingCompanyRow>[] = [
      {
        field: 'name',
        headerName: 'Company',
        pinned: 'left' as const,
        minWidth: 160,
        flex: 1,
        headerComponent: ExpandAllHeader,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: SourcingCompanyNameLink,
        },
      },
      {
        field: 'logoUrl',
        headerName: 'Logo',
        width: 80,
        hide: true,
      },
      {
        field: 'domain',
        headerName: 'Domain',
        width: 160,
      },
      {
        field: 'website',
        headerName: 'Website',
        width: 200,
        hide: true,
      },
      {
        field: 'legalName',
        headerName: 'Legal Name',
        width: 200,
        hide: true,
      },
      {
        field: 'aliases',
        headerName: 'Aliases',
        width: 180,
        valueFormatter: companyStringArray,
        hide: true,
      },
      {
        field: 'shortDescription',
        headerName: 'Short Description',
        minWidth: 220,
        flex: 1,
      },
      {
        field: 'longDescription',
        headerName: 'Long Description',
        minWidth: 300,
        flex: 2,
        hide: true,
      },
      {
        field: 'sector',
        headerName: 'Sector',
        minWidth: 180,
        flex: 1,
      },
      {
        field: 'industry',
        headerName: 'Industry',
        width: 160,
      },
      {
        field: 'latestFundingStage',
        headerName: 'Latest Funding Stage',
        width: 160,
        cellRenderer: FundingStageCellRenderer,
      },
      {
        field: 'totalFunding',
        headerName: 'Total Funding',
        width: 130,
        type: 'numericColumn',
        valueFormatter: currencyShort,
      },
      {
        field: 'numFundingRounds',
        headerName: '# Funding Rounds',
        width: 140,
        type: 'numericColumn',
        valueFormatter: integer,
      },
      {
        field: 'annualRevenue',
        headerName: 'Annual Revenue',
        width: 140,
        type: 'numericColumn',
        valueFormatter: currencyShort,
      },
      {
        field: 'foundedYear',
        headerName: 'Founded',
        width: 100,
        type: 'numericColumn',
      },
      {
        field: 'headcount',
        headerName: 'Headcount',
        width: 110,
      },
      {
        field: 'ceoName',
        headerName: 'CEO',
        width: 160,
      },
      {
        field: 'founders',
        headerName: 'Founders',
        width: 200,
        valueFormatter: companyStringArray,
      },
      {
        field: 'primaryLocation',
        headerName: 'Primary Location',
        minWidth: 160,
        flex: 1,
      },
      {
        field: 'secondaryLocation',
        headerName: 'Secondary Location',
        width: 180,
        hide: true,
      },
      {
        field: 'acquired',
        headerName: 'Acquired',
        width: 100,
        valueFormatter: yesNo,
      },
      {
        field: 'acquiredBy',
        headerName: 'Acquired By',
        width: 160,
      },
      {
        field: 'emails',
        headerName: 'Emails',
        width: 220,
        valueFormatter: companyStringArray,
        hide: true,
      },
      {
        field: 'phones',
        headerName: 'Phones',
        width: 180,
        valueFormatter: companyStringArray,
        hide: true,
      },
      {
        field: 'linkedinUrl',
        headerName: 'LinkedIn',
        width: 160,
        hide: true,
      },
      {
        field: 'crunchbaseUrl',
        headerName: 'Crunchbase',
        width: 160,
        hide: true,
      },
      {
        field: 'pitchbookUrl',
        headerName: 'PitchBook',
        width: 160,
        hide: true,
      },
      {
        field: 'facebookUrl',
        headerName: 'Facebook',
        width: 160,
        hide: true,
      },
      {
        field: 'twitterUrl',
        headerName: 'Twitter / X',
        width: 160,
        hide: true,
      },
    ];

    const person: ColDef<ISourcingPersonRow>[] = [
      {
        field: 'fullName',
        headerName: 'Name',
        pinned: 'left' as const,
        minWidth: 160,
        flex: 1,
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: 'title',
        headerName: 'Title',
        minWidth: 180,
        flex: 1,
      },
      {
        field: 'headline',
        headerName: 'Headline',
        minWidth: 220,
        flex: 2,
      },
      {
        field: 'department',
        headerName: 'Department',
        width: 160,
      },
      {
        field: 'seniority',
        headerName: 'Seniority',
        width: 130,
      },
      {
        field: 'companyDomain',
        headerName: 'Company',
        width: 180,
      },
      {
        field: 'city',
        headerName: 'City',
        width: 140,
      },
      {
        field: 'state',
        headerName: 'State',
        width: 100,
      },
      {
        field: 'country',
        headerName: 'Country',
        width: 120,
      },
      {
        field: 'emails',
        headerName: 'Emails',
        width: 220,
        valueFormatter: personStringArray,
      },
      {
        field: 'phones',
        headerName: 'Phones',
        width: 180,
        valueFormatter: personStringArray,
        hide: true,
      },
      {
        field: 'linkedinUrl',
        headerName: 'LinkedIn',
        width: 180,
        hide: true,
      },
      {
        field: 'githubUrl',
        headerName: 'GitHub',
        width: 160,
        hide: true,
      },
      {
        field: 'twitterUrl',
        headerName: 'Twitter / X',
        width: 160,
        hide: true,
      },
      {
        field: 'facebookUrl',
        headerName: 'Facebook',
        width: 160,
        hide: true,
      },
      {
        field: 'addedAt',
        headerName: 'Added',
        width: 130,
      },
    ];

    return { company, person };
  }, []);
}
